// API клиент модуля «Витрина».
// Контракт согласован с бэком (B) — см. сообщение в Outline ТЗ от 2026-06-10.
// Endpoint'ы под /api/max/showcase/:channelUUID — auth через cookie.

import { genRequestId } from '@/lib/requestId'

export type ShowcaseStatus =
  | 'locked'
  | 'intro'
  | 'consent'
  | 'choosing'
  | 'processing'
  | 'active'
  | 'disabled'
  | 'grace_3d'

export type WhatToPublish = 'all' | 'long' | 'manual'

export interface ShowcaseSettings {
  whatToPublish?: WhatToPublish
  backfillRequested?: boolean
}

export interface ShowcaseConsent {
  acceptedAt: string // ISO
  consentVersion: string
  userId: string
  checkboxes: [boolean, boolean, boolean]
  previewUrl?: string
}

export interface ShowcaseState {
  status: ShowcaseStatus
  settings: ShowcaseSettings
  consent?: ShowcaseConsent
  activatedAt?: string
  disabledAt?: string
  pendingDeindexAt?: string // если grace_3d
  graceDaysLeft?: number // если grace_3d (3/2/1/0)
}

export interface ConsentPayload {
  consentVersion: string
  checkboxes: [boolean, boolean, boolean]
  previewUrl?: string
}

export interface ChoosingPayload {
  whatToPublish: WhatToPublish
  backfillRequested?: boolean
}

// Версия условий согласия — нужно бампать при изменении legal-формулировок.
// Бэк сохраняет это в consent metadata для аудита.
export const CONSENT_VERSION = 'v1-2026-06'

class ShowcaseApiError extends Error {
  code: string
  status: number
  requestId: string
  constructor(code: string, status: number, requestId: string, message?: string) {
    super(message || code)
    this.code = code
    this.status = status
    this.requestId = requestId
  }
}

async function call<T = unknown>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // Cheap request-id (см. контракт §4): UUID v4 в X-Request-Id, бэк его
  // читает в req.id и пишет в свои логи. SQL-сшивка фронт↔бэк работает.
  const requestId = genRequestId()
  const res = await fetch(`/api/max/showcase${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Request-Id': requestId,
      ...init?.headers,
    },
  })
  if (!res.ok) {
    let body: { error?: string; message?: string } = {}
    try {
      body = await res.json()
    } catch {}
    throw new ShowcaseApiError(
      body.error || `HTTP_${res.status}`,
      res.status,
      requestId,
      body.message,
    )
  }
  return res.json()
}

export function getShowcase(channelUUID: string): Promise<ShowcaseState> {
  return call<ShowcaseState>(`/${encodeURIComponent(channelUUID)}`)
}

export function activateIntro(channelUUID: string): Promise<ShowcaseState> {
  return call<ShowcaseState>(`/${encodeURIComponent(channelUUID)}/activate/intro`, {
    method: 'POST',
  })
}

export function activateConsent(
  channelUUID: string,
  payload: ConsentPayload,
): Promise<ShowcaseState> {
  return call<ShowcaseState>(
    `/${encodeURIComponent(channelUUID)}/activate/consent`,
    { method: 'POST', body: JSON.stringify(payload) },
  )
}

export function activateChoosing(
  channelUUID: string,
  payload: ChoosingPayload,
): Promise<ShowcaseState> {
  return call<ShowcaseState>(
    `/${encodeURIComponent(channelUUID)}/activate/choosing`,
    { method: 'POST', body: JSON.stringify(payload) },
  )
}

// PATCH /api/max/showcase/:channelUUID (B 16.06, commit a655b9d9)
// Partial update для смены настроек в active/disabled/grace_3d.
// 409 STATE_NOT_EDITABLE для intro/consent/choosing/processing/locked
// с payload { currentStatus, editableStates } для machine-логики.
export interface UpdateShowcaseSettingsPayload {
  whatToPublish?: WhatToPublish
  removeShorts?: boolean
}

export function updateShowcaseSettings(
  channelUUID: string,
  payload: UpdateShowcaseSettingsPayload,
): Promise<ShowcaseState> {
  return call<ShowcaseState>(`/${encodeURIComponent(channelUUID)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export function disableShowcase(channelUUID: string): Promise<ShowcaseState> {
  return call<ShowcaseState>(`/${encodeURIComponent(channelUUID)}/disable`, {
    method: 'POST',
  })
}

export function enableShowcase(channelUUID: string): Promise<ShowcaseState> {
  return call<ShowcaseState>(`/${encodeURIComponent(channelUUID)}/enable`, {
    method: 'POST',
  })
}

// ───────── Posts list (B6) ─────────

// Контракт B6 от 11.06: 6 статусов вместо 4 — добавлены `processing` (между
// queued и published) и `quality_rejected` отдельно от `deindexed`.
// `failed` — пост не дошёл до публикации из-за технической ошибки.
export type PostStatus =
  | 'queued'
  | 'processing'
  | 'published'
  | 'quality_rejected'
  | 'deindexed'
  | 'failed'

export type PostsSort = 'published_at_desc' | 'visits_desc' | 'score_desc'

export interface ShowcasePost {
  postId: string // UUID портала — то же что в B8/B18
  sourcePostId: string // MAX post id
  slug: string
  url: string // полный https://telegraphyx.ru/channels/p/...
  title: string
  description: string
  status: PostStatus
  publishedAt: string | null // ISO
  deindexedAt: string | null // ISO, если status === 'deindexed'
  qualityScore: number | null // 0..100
  noindex: boolean // true → опубликован, но не индексируется
  visitsTotal: number // агрегат от портала за всё время (до B7 — основной счётчик)
}

// counts_by_status — всегда полный расклад от портала (даже при ?status=X total
// урезан до отфильтрованного, а counts остаются полными). Это даёт стабильные
// бейджи-счётчики на чипах при переключении фильтров.
export type PostsCountsByStatus = Record<PostStatus, number>

export interface ShowcasePostsList {
  items: ShowcasePost[]
  total: number
  limit: number
  offset: number
  countsByStatus?: PostsCountsByStatus
}

export interface PostsQuery {
  status?: PostStatus
  limit?: number
  offset?: number
  sort?: PostsSort
}

/**
 * Список постов канала с агрегатами (visits/searchClicks/quality) и статусом.
 * Поля совпадают с тем что отдаёт SEO-портал — фронт рендерит таблицу
 * не глядя на источник (transit vs local store решает B).
 */
export function getShowcasePosts(
  channelUUID: string,
  query: PostsQuery = {},
): Promise<ShowcasePostsList> {
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  params.set('limit', String(query.limit ?? 20))
  params.set('offset', String(query.offset ?? 0))
  params.set('sort', query.sort ?? 'published_at_desc')
  return call<ShowcasePostsList>(`/${encodeURIComponent(channelUUID)}/posts?${params}`)
}

// ───────── Overview + Per-post metrics (B7) ─────────

// B-фикс 17.06.2026 (commit faff6e98): единый ключ `visits` везде —
// overview last7d/last30d и per-post totals. Поле `total` больше не возвращается.
export interface VisitsBucket {
  visits: number
  yandex: number
  google: number
  other: number
}

export interface SearchQuery {
  query: string
  impressions: number
  clicks: number
  position: number
  // ctr = clicks / impressions (0.0-1.0). Опциональный: бэк начал слать после
  // ТЗ per-post-seo-metrics (согласование B ↔ C ↔ A от 2026-07-01). Старые
  // ответы без ctr не ломают тип.
  ctr?: number
}

export interface SearchWindow {
  impressions: number
  clicks: number
  avgPosition: number
  topQueries: SearchQuery[] // до 10
}

// Контракт B15 (11.06): overview searchMetrics. Site-scope от Я.Вебмастера
// сейчас, GSC будет автопереключать source через hybrid → gsc.
export type SearchMetrics =
  | { status: 'pending_oauth' }
  | {
      status: 'ready'
      source: 'yandex_webmaster' | 'gsc' | 'hybrid'
      scope: 'site' | 'channel'
      snapshotAt: string // ISO, лаг Я.Вебмастера ~5 дней
      last7d: SearchWindow
      last30d: SearchWindow
    }

// Контракт B17 (11.06) + расширение per-post-seo-metrics (01.07):
// per-post searchMetrics — flat snapshot last30d, без выбора окна.
// last7d не отдаётся, потому что Я.Вебмастер имеет 5-дневный лаг → 2 дня
// реальных данных = шум (согласовано B ↔ A). avgPosition = null если
// impressions=0. topQueries отфильтрованы `impressions >= 10` на C/B стороне.
export type PostSearchMetrics =
  | { status: 'pending_oauth' }
  | {
      status: 'ready'
      source: 'yandex_webmaster' | 'gsc' | 'hybrid'
      scope: 'post'
      snapshotAt: string
      impressions: number
      clicks: number
      avgPosition: number | null
      topQueries: SearchQuery[] // top-5 по impressions desc
    }

export interface ShowcaseOverview {
  visits: { last7d: VisitsBucket; last30d: VisitsBucket }
  qualityRejectedTotal: number
  searchMetrics: SearchMetrics
}

export function getShowcaseOverview(channelUUID: string): Promise<ShowcaseOverview> {
  return call<ShowcaseOverview>(`/${encodeURIComponent(channelUUID)}/overview`)
}

export interface PostMetricsDay {
  date: string // YYYY-MM-DD (МСК)
  visits: number
  yandex: number
  google: number
  other: number
}

export interface PostMetrics {
  postId: string
  days: PostMetricsDay[]
  totals: VisitsBucket
  searchMetrics: PostSearchMetrics
}

export function getPostMetrics(
  channelUUID: string,
  postId: string,
  days: number = 30,
): Promise<PostMetrics> {
  const clamped = Math.max(1, Math.min(90, days))
  return call<PostMetrics>(
    `/${encodeURIComponent(channelUUID)}/posts/${encodeURIComponent(postId)}/metrics?days=${clamped}`,
  )
}

// ───────── Quality Rejected (B10) ─────────

export type QualityRejectedLevel = 'L1' | 'L2' | 'L3'

export interface QualityRejectedItem {
  id: string
  sourcePostId: string
  level: QualityRejectedLevel
  reason: string
  details?: unknown
  createdAt: string // ISO
}

export interface QualityRejectedList {
  items: QualityRejectedItem[]
  total: number
  limit: number
  offset: number
}

/**
 * Список постов, отвергнутых автоматическими фильтрами качества (L1/L2/L3).
 * Свежие сверху. Для UI A9 («N постов не прошли проверку»).
 */
export function getQualityRejected(
  channelUUID: string,
  opts: { limit?: number; offset?: number; level?: QualityRejectedLevel } = {},
): Promise<QualityRejectedList> {
  const params = new URLSearchParams()
  params.set('limit', String(opts.limit ?? 50))
  params.set('offset', String(opts.offset ?? 0))
  if (opts.level) params.set('level', opts.level)
  return call<QualityRejectedList>(`/${encodeURIComponent(channelUUID)}/quality-rejected?${params}`)
}

// ───────── Deindex Post (B8 transit) ─────────

/**
 * Убрать пост из поиска — транзит через telegraphyx к порталу.
 * Идемпотентно: повторный вызов вернёт `{ ok: true, deindexedCount: 0 }`.
 */
export function deindexPost(
  channelUUID: string,
  postId: string,
): Promise<{ ok: boolean; deindexedCount: number }> {
  return call<{ ok: boolean; deindexedCount: number }>(
    `/${encodeURIComponent(channelUUID)}/posts/${encodeURIComponent(postId)}/deindex`,
    { method: 'POST' },
  )
}

// Reindex (B commit af9d91c6, 18.06.2026) — вернуть deindexed-пост в поиск.
// status описывает что сделал бэк:
//  - restored_from_archive: HTML был в архиве портала, восстановлен мгновенно
//  - queued_for_render: нужно перерисовать, поставлен в очередь (~30-300с)
//  - already_published: пост уже был published, no-op (idempotent)
export type ReindexStatus = 'restored_from_archive' | 'queued_for_render' | 'already_published'

export interface ReindexResponse {
  ok: boolean
  status: ReindexStatus
  estimatedSeconds: number
}

export function reindexPost(
  channelUUID: string,
  postId: string,
): Promise<ReindexResponse> {
  return call<ReindexResponse>(
    `/${encodeURIComponent(channelUUID)}/posts/${encodeURIComponent(postId)}/reindex`,
    { method: 'POST' },
  )
}

// ───────── Bulk Backfill Archive (C9 transit) ─────────

// C9 sync response (контракт B 15.06.2026 после mapping):
// - sourcePostsCount: всего постов в исходном канале (до фильтрации)
// - receivedCount: что прилетело порталу на бэкфилл
// - queuedCount: реально в очередь (после L1 + UNIQUE-check)
// - filteredOutCount: не прошли quality-фильтр L1 (короткие/невалидные)
// - duplicateCount: уже опубликованы (dup по source_post_id)
// Поддерживаем snake_case fallback на случай отставания mapping'а в B.
export interface BulkBackfillResponse {
  ok: boolean
  sourcePostsCount?: number
  receivedCount?: number
  queuedCount?: number
  filteredOutCount?: number
  duplicateCount?: number
  postsCount?: number // legacy алиас, удалить после стабильности
  estimatedCompletionHours: number
  rateLimitPerHour: number
}

/**
 * Запустить массовую обработку архивных постов канала.
 * До 1000 на batch, rate-limit 100/час (см. ответ от C 11.06).
 * 409 SHOWCASE_NOT_ACTIVE — если state.status !== 'active' (фронт обязан
 * дизейблить кнопку в этих случаях, но валидация на бэке — на всякий).
 */
export function bulkBackfillArchive(
  channelUUID: string,
): Promise<BulkBackfillResponse> {
  return call<BulkBackfillResponse>(
    `/${encodeURIComponent(channelUUID)}/bulk-backfill-archive`,
    { method: 'POST' },
  )
}

// ───────── Preview HTML (B18 transit) ─────────

export type PreviewStatus = 'published' | 'deindexed' | 'quality_rejected' | 'pending' | 'failed'

export interface PreviewMeta {
  status: PreviewStatus
  qualityScore: number | null
  noindex: boolean
}

export interface PreviewResponse {
  html: string
  meta: PreviewMeta
}

/**
 * Получить готовый HTML preview страницы поста с портала (для browser-frame в A5).
 *
 * B18 endpoint: GET /api/max/showcase/posts/:postID/preview-html?channelUUID=<uuid>
 * - channelUUID — обязательный query param для ownership-check (без него 400)
 * - Авторизация через existing cookie (MaxWebAppGuard на бэке)
 * - SHOWCASE_RENDER_SECRET к порталу держит B — фронт его не видит
 *
 * 404 коды от портала пробрасываются как есть: POST_NOT_FOUND / NOT_PUBLISHED / FILE_NOT_FOUND
 */
export async function getShowcasePreview(
  postID: string,
  channelUUID: string,
): Promise<PreviewResponse> {
  const requestId = genRequestId()
  const res = await fetch(
    `/api/max/showcase/posts/${encodeURIComponent(postID)}/preview-html?channelUUID=${encodeURIComponent(channelUUID)}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId,
      },
    },
  )
  if (!res.ok) {
    let body: { error?: string; message?: string } = {}
    try { body = await res.json() } catch {}
    throw new ShowcaseApiError(
      body.error || `HTTP_${res.status}`,
      res.status,
      requestId,
      body.message,
    )
  }
  return res.json()
}

export { ShowcaseApiError }
